import React, { useState, useEffect, useCallback } from "react";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { registerLocale } from "react-datepicker";
import moment from "moment";

import es from "date-fns/locale/es";
import {
  Picker,
  Container,
  ImgContainer,
  Icon,
  ReactToolTip,
  ToolTipImg,
  ToolTipImgContainer
} from "./Styles";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("es", es);

export const launchPicker = (element) => {
  if (!element) {
    return;
  }
  element.click();
};

const DatePickerInput = (props) => {
  const {
    value,
    format,
    placeholder,
    onChange,
    monthDropdown,
    yearDropdown,
    showTooltip,
    error,
    className,
    maxDate,
    minDate,
    onBlur,
    setTouchedIcon,
    isSummary,
    isDisabled,
    errorMessage
  } = props;
  const [startDate, setStartDate] = useState(value ? new Date(value) : "");
  const [internalError, setInternalError] = useState(null);
  // Mostrar tooltip si hay error (externo o interno) y showTooltip está habilitado
  const finalError = error || internalError;
  const renderTooltip = showTooltip && finalError;
  const { t } = useTranslation();

  const validateDate = useCallback(
    (date) => {
      if (!date || date === "") {
        setInternalError(null);
        return true;
      }

      const selectedMoment = moment(date);

      // Validar maxDate
      if (maxDate && selectedMoment.isAfter(moment(maxDate), "day")) {
        setInternalError(t("datepicker.error.futureDate"));
        return false;
      }

      // Validar minDate
      if (minDate && selectedMoment.isBefore(moment(minDate), "day")) {
        setInternalError(t("datepicker.error.pastDate"));
        return false;
      }

      setInternalError(null);
      return true;
    },
    [maxDate, minDate, t]
  );

  // Validar fecha cuando cambia el valor o las restricciones
  useEffect(() => {
    if (startDate && startDate !== "") {
      validateDate(startDate);
    } else {
      setInternalError(null);
    }
  }, [startDate, validateDate]);

  const handleCalendarClose = () => {
    if (startDate === "") {
      setTouchedIcon(true);
    }
  };

  const handleCalendarOpen = () => {
    setTouchedIcon(false);
  };

  return (
    <Container className={className} isSummary={isSummary}>
      <Picker
        data-testid="datepicker-test"
        id="datepicker"
        isSummary={isSummary}
        isDisabled={isDisabled}
        selected={startDate}
        locale="es"
        dateFormat={format}
        placeholderText={placeholder}
        showMonthDropdown={monthDropdown}
        showYearDropdown={yearDropdown}
        scrollableYearDropdown={true}
        maxDate={maxDate}
        dropdownMode="select"
        onChange={(date) => {
          onChange(date);
          setStartDate(date);
        }}
        onCalendarClose={handleCalendarClose}
        onCalendarOpen={handleCalendarOpen}
        {...props}
      />
      {renderTooltip && (
        <>
          <ToolTipImgContainer data-tip={true} data-for="datepicker-error-tooltip">
            <ToolTipImg src="/assets/images/error.png" alt="error" />
          </ToolTipImgContainer>

          <ReactToolTip id="datepicker-error-tooltip" place="bottom" type="error" effect="solid">
            <div className="error-tooltip__container">
              <p className="error-tooltip__text--title">
                {errorMessage || t(`${error}`) || "Selecciona una fecha válida"}
              </p>
            </div>
          </ReactToolTip>
        </>
      )}
      <ImgContainer
        isDisabled={isDisabled}
        onClick={() => {
          launchPicker(document.getElementById(props.id));
          onBlur();
        }}
        id="picker-icon"
      >
        <Icon src={isSummary ? "/assets/images/imageCalendarSummary_.png" : "/assets/images/calendar_icon.png"}
          alt="Date picker" isSummary={isSummary}
          isDisabled={isDisabled} />
      </ImgContainer>
    </Container>
  );
};

DatePickerInput.propTypes = {
  value: propTypes.string,
  format: propTypes.string,
  error: propTypes.string,
  errorMessage: propTypes.string,
  showTooltip: propTypes.bool,
  placeholder: propTypes.string,
  monthDropdown: propTypes.bool,
  yearDropdown: propTypes.bool,
  onChange: propTypes.func.isRequired,
  id: propTypes.string,
  className: propTypes.string,
  maxDate: propTypes.instanceOf(Date),
  minDate: propTypes.instanceOf(Date),
  onBlur: propTypes.func,
  isSummary: propTypes.bool,
  isDisabled: propTypes.bool,
  setTouchedIcon: propTypes.func
};

DatePickerInput.defaultProps = {
  maxDate: undefined,
  minDate: undefined,
  value: "",
  className: "",
  format: "yyyy-MM-dd",
  placeholder: "",
  monthDropdown: true,
  yearDropdown: true,
  error: null,
  errorMessage: null,
  showTooltip: true,
  id: "",
  isSummary: false,
  isDisabled: false,
  onBlur: () => {},
  setTouchedIcon: () => {}
};

export default DatePickerInput;
